import { color, fontSize, space } from '@/constants/tokens';
import { Image, ImageSource } from 'expo-image';
import { useMemo } from 'react';
import { Platform, StyleSheet, View } from 'react-native';

import { Arrow } from '@/components/Carousel';
import { useFrameWidth } from '@/components/ui/pageFrame';
import { Gesture, GestureDetector } from 'react-native-gesture-handler';
import Animated, {
  type SharedValue,
  useAnimatedStyle,
  useSharedValue,
  withSpring,
  withTiming,
} from 'react-native-reanimated';

// A swipeable pile of polaroids for the "Experiences our members loved"
// section. Cards sit in a loose, hand-tossed stack — each one tilted at its
// own random angle (not a uniform fan) — and the top card can be flicked left
// or right to send it to the back, revealing the next.
//
// This is intentionally separate from the shared `PolaroidCardStack`: that one
// uses a deterministic, count-driven fan tuned for the older nonmember sections
// (4 small cards). This v3 version is a large, full-width pile with random
// tilts and is owned by this screen.

const MEMBER_LOVED_EXPERIENCES: MemberLovedPolaroid[] = [
  {
    source: require('@/assets/images/polaroids/naru.webp'),
    caption: 'Members only at Naru',
  },
  {
    source: require('@/assets/images/polaroids/kopi.jpeg'),
    caption: 'Early Access at Kopitiam Lah',
  },
  {
    source: require('@/assets/images/polaroids/croissant-run.jpg'),
    caption: 'The Croissant Run with Lavonne',
  },
  {
    source: require('@/assets/images/polaroids/conservation-lab.jpg'),
    caption: 'Secrets of the Conservation Lab @ Museum of Art & Photography',
  },
  {
    source: require('@/assets/images/polaroids/doubble.jpg'),
    caption: 'Pre-launch at Bar Doubble',
  },
  {
    source: require('@/assets/images/polaroids/nila.jpg'),
    caption: '12 course menu @ Nila',
  },
  {
    source: require('@/assets/images/polaroids/sixth-sense.jpg'),
    caption: 'Exclusive preview: The Sixth Sense immersive festival',
  },
  {
    source: require('@/assets/images/polaroids/spirit-school.jpg'),
    caption: 'Spirit School @ The Good Craft Co',
  },
];

export interface MemberLovedPolaroid {
  source: ImageSource;
  caption: string;
}

interface MemberLovedPolaroidStackProps {
  // Horizontal padding around the stack, used to size the card to the content
  // width and to leave room for tilted corners to peek out without clipping.
  horizontalPadding?: number;
}

const SPRING_CONFIG = { stiffness: 600, damping: 50 };
const DRAG_ROTATION_FACTOR = 0.06; // extra rotation per px dragged
// Range (degrees) for each card's resting random tilt.
const MAX_RANDOM_TILT = 9;
// Cards deeper than this in the pile share the bottom card's resting position,
// so a long list stays a tight pile instead of drifting.
const MAX_VISIBLE_DEPTH = 4;
// Polaroid proportions: the photo is square, the frame adds an even border on
// the sides/top, and a chunky bottom border below the caption like a real
// polaroid. The card height is intrinsic — it grows with the caption — so we
// only fix the paddings here, not a total height.
const FRAME_PADDING = 14;
const FRAME_BOTTOM_PADDING = 22;
// Photo is portrait 3:4 (width:height), so its height is 4/3 of its width.
const PHOTO_ASPECT = 4 / 3;
// Caption typography, used both for styling and to reserve enough container
// height for the tallest (two-line) caption so nothing clips.
const CAPTION_LINE_HEIGHT = fontSize.large * 1.3;
const CAPTION_MARGIN_TOP = 12;
const CAPTION_MAX_LINES = 2;

// A random tilt in [-MAX_RANDOM_TILT, MAX_RANDOM_TILT]. Rolled once per card on
// the JS thread (Math.random() can't run inside a reanimated worklet) and held
// stable for the component's life, so the pile looks hand-tossed without
// re-rolling on every render.
function randomTilt(): number {
  return (Math.random() * 2 - 1) * MAX_RANDOM_TILT;
}

export default function MemberLovedPolaroidStack({
  horizontalPadding = 16,
}: MemberLovedPolaroidStackProps) {
  const screenWidth = useFrameWidth();
  // Full content width, inset slightly so the tilted back cards can peek out at
  // the sides without their corners clipping against the screen edge.
  const cardWidth =
    screenWidth > 480 ? 320 : screenWidth - horizontalPadding * 2 - 24;
  const photoWidth = cardWidth - FRAME_PADDING * 2;
  const photoHeight = photoWidth * PHOTO_ASPECT;
  // Each card sizes to its own caption (1 or 2 lines). The container, though,
  // must be tall enough for the worst case so a two-line caption never clips and
  // the back-card offsets always have room.
  const maxCardHeight =
    FRAME_PADDING +
    photoHeight +
    CAPTION_MARGIN_TOP +
    CAPTION_LINE_HEIGHT * CAPTION_MAX_LINES +
    FRAME_BOTTOM_PADDING;

  // Animation state, designed so state advances SYNCHRONOUSLY on dismiss and no
  // animation-completion callback is needed to commit it (callbacks racing the
  // next gesture were the source of the "second swipe does nothing / card
  // resurfaces" bug):
  //   position — monotonic counter; the top card is `position % total`.
  //   dragX    — live drag of the current top card; reset to 0 the instant a
  //              dismiss is committed so the next top card is immediately clean.
  //   exitX    — drives ONLY the just-dismissed card (now at the back) as it
  //              flies from the fling position home. Independent of dragX, so an
  //              interrupted exit can never affect the new top card.
  //   enterX   — mirrors exitX for the web-only "previous" chevron: drives
  //              ONLY the card that's returning to the top as it flies in
  //              from off-screen. dragX and enterX are never both nonzero at
  //              once (one is drag-driven, the other chevron-driven), so the
  //              top card's transform can just sum them.
  const position = useSharedValue(0);
  const dragX = useSharedValue(0);
  const exitX = useSharedValue(0);
  const enterX = useSharedValue(0);
  const items = MEMBER_LOVED_EXPERIENCES;
  const total = items.length;

  // Stable random tilt per card, rolled once. A plain number[] is readable
  // inside worklets.
  const tilts = useMemo(() => items.map(() => randomTilt()), [items]);

  const panGesture = Gesture.Pan()
    .minDistance(10)
    .activeOffsetX([-12, 12])
    .failOffsetY([-12, 12])
    .onUpdate((e) => {
      dragX.value = e.translationX;
    })
    .onEnd((e) => {
      const shouldDismiss =
        Math.abs(e.translationX) > cardWidth * 0.35 ||
        Math.abs(e.velocityX) > 500;

      if (shouldDismiss) {
        // Commit the dismiss immediately: hand the drag offset to exitX, advance
        // the counter, and zero dragX. The new top card is correct on the very
        // next frame. exitX then eases home so the outgoing card animates to the
        // back of the pile — purely cosmetic, nothing depends on it finishing.
        const direction = e.translationX > 0 ? 1 : -1;
        const flungFrom = Math.max(Math.abs(e.translationX), cardWidth * 0.6);
        exitX.value = direction * flungFrom;
        position.value += 1;
        dragX.value = 0;
        exitX.value = withTiming(0, { duration: 320 });
      } else {
        dragX.value = withSpring(0, SPRING_CONFIG);
      }
    });

  // Web-only chevron nav (desktop has no swipe gesture — same rationale as
  // Carousel.tsx's affordance row). No counter, per the design: this stack
  // is an endless loop (position is an unbounded counter, always read mod
  // `total`), not a paged sequence with a knowable "total" to count toward.
  const goToNext = () => {
    // Mirrors a real leftward swipe-dismiss: jump the outgoing card to a
    // flung offset, commit the position change (the next card is already
    // sitting underneath, so it needs no entrance animation of its own),
    // then ease the outgoing card home to the back of the pile.
    const direction = -1;
    exitX.value = direction * cardWidth * 0.6;
    position.value += 1;
    dragX.value = 0;
    exitX.value = withTiming(0, { duration: 320 });
  };

  const goToPrevious = () => {
    // Undoes a dismiss: the card that was last sent to the back becomes top
    // again, so it flies in from the same side a dismiss sends cards to
    // (see `direction` above) rather than snapping into place instantly.
    position.value -= 1;
    enterX.value = -cardWidth * 0.6;
    enterX.value = withTiming(0, { duration: 320 });
  };

  // A single gesture wraps the whole pile rather than one per card: the cards
  // are absolutely stacked and overlapping, so per-card detectors fight over
  // the touch and end up swallowing it. One detector on the container always
  // drives whichever card is currently on top.
  return (
    <View style={styles.wrapper}>
      <GestureDetector gesture={panGesture}>
        <View
          style={[
            styles.container,
            { width: cardWidth, height: maxCardHeight + 40 },
          ]}
        >
          {items.map((item, index) => (
            <PolaroidCard
              key={index}
              item={item}
              index={index}
              total={total}
              tilts={tilts}
              position={position}
              dragX={dragX}
              exitX={exitX}
              enterX={enterX}
              cardWidth={cardWidth}
              photoWidth={photoWidth}
              photoHeight={photoHeight}
            />
          ))}
        </View>
      </GestureDetector>

      {Platform.OS === 'web' ? (
        <View style={styles.chevronRow}>
          <Arrow side="left" visible onPress={goToPrevious} />
          <Arrow side="right" visible onPress={goToNext} />
        </View>
      ) : null}
    </View>
  );
}

interface PolaroidCardProps {
  item: MemberLovedPolaroid;
  index: number;
  total: number;
  tilts: number[];
  position: SharedValue<number>;
  dragX: SharedValue<number>;
  exitX: SharedValue<number>;
  enterX: SharedValue<number>;
  cardWidth: number;
  photoWidth: number;
  photoHeight: number;
}

function PolaroidCard({
  item,
  index,
  total,
  tilts,
  position,
  dragX,
  exitX,
  enterX,
  cardWidth,
  photoWidth,
  photoHeight,
}: PolaroidCardProps) {
  const animatedStyle = useAnimatedStyle(() => {
    // Position of this card in the pile: 0 = top, increasing toward the back.
    // position grows unboundedly, so use a true positive modulo.
    const raw = (index - position.value) % total;
    const stackOffset = (raw + total) % total;
    const zIndex = total - stackOffset;
    const isTopCard = stackOffset === 0;

    // Clamp depth so a long list doesn't drift past the visible pile.
    const depth = Math.min(stackOffset, MAX_VISIBLE_DEPTH);
    // Each card rests at its own random tilt; cards further back also shift
    // down/right slightly so the pile reads as a stack of physical photos.
    const restingTilt = tilts[index];
    const restingY = depth * 4;
    const restingX = depth * 2;

    if (isTopCard) {
      // Active top card — follows the drag and tilts toward the flick.
      // enterX is nonzero only right after a "previous" chevron press,
      // easing this card in from off-screen; it and dragX are never both
      // nonzero at once, so summing them is safe.
      const x = dragX.value + enterX.value;
      return {
        zIndex,
        transform: [
          { translateX: x },
          { translateY: 0 },
          { rotate: `${restingTilt + x * DRAG_ROTATION_FACTOR}deg` },
        ],
      };
    }

    // The just-dismissed card sits at the very back (stackOffset === total - 1)
    // and, while exitX is decaying, flies from the fling position into its
    // resting spot. zIndex is forced to 0 so it slides in BEHIND the rest of the
    // pile rather than over the new top card.
    const isExiting = stackOffset === total - 1 && exitX.value !== 0;
    if (isExiting) {
      return {
        zIndex: 0,
        transform: [
          { translateX: restingX + exitX.value },
          { translateY: restingY },
          {
            rotate: `${restingTilt + exitX.value * DRAG_ROTATION_FACTOR}deg`,
          },
        ],
      };
    }

    // Resting card in the pile.
    return {
      zIndex,
      transform: [
        { translateX: restingX },
        { translateY: restingY },
        { rotate: `${restingTilt}deg` },
      ],
    };
  });

  // Only the top card shows its caption; back cards would otherwise bleed their
  // text out past the edges of the card on top.
  const captionStyle = useAnimatedStyle(() => {
    const raw = (index - position.value) % total;
    const stackOffset = (raw + total) % total;
    return { opacity: stackOffset === 0 ? 1 : 0 };
  });

  return (
    <Animated.View style={[styles.card, { width: cardWidth }, animatedStyle]}>
      <Image
        source={item.source}
        style={[styles.photo, { width: photoWidth, height: photoHeight }]}
        contentFit="cover"
        transition={200}
      />
      <Animated.Text
        style={[styles.caption, captionStyle]}
        numberOfLines={CAPTION_MAX_LINES}
      >
        {item.caption}
      </Animated.Text>
    </Animated.View>
  );
}

const styles = StyleSheet.create({
  wrapper: {
    alignItems: 'center',
  },
  chevronRow: {
    flexDirection: 'row',
    gap: space.md,
    marginTop: space.md,
  },
  container: {
    alignSelf: 'center',
    alignItems: 'center',
    // Top-align so every card's photo sits at the same Y regardless of how tall
    // its caption makes the card — otherwise centered cards of different heights
    // would shift the photo up/down and the pile would jitter on each swipe.
    justifyContent: 'flex-start',
  },
  card: {
    position: 'absolute',
    backgroundColor: color.background,
    paddingTop: FRAME_PADDING,
    paddingHorizontal: FRAME_PADDING,
    paddingBottom: FRAME_BOTTOM_PADDING,
    shadowColor: color.brandDark,
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.25,
    shadowRadius: 10,
    elevation: 6,
  },
  photo: {
    backgroundColor: color.backgroundSecondary,
  },
  caption: {
    fontSize: fontSize.base,
    lineHeight: CAPTION_LINE_HEIGHT,
    color: color.textDark,
    marginTop: CAPTION_MARGIN_TOP,
    textAlign: 'center',
  },
});
